<template>
  <div class="main-content">
    <!-- 顶部分类区域，仅桌面端显示 -->
    <div v-if="!isMobile" class="categories">
      <div
        v-for="category in categories"
        :key="category"
        @click="selectCategory(category)"
        :class="{ active: localSelectedCategory === category }"
        class="category-item"
      >
        <font-awesome-icon :icon="getCategoryIcon(category)" class="category-icon" />
        {{ category }}
      </div>
    </div>

    <!-- 分类工具列表 -->
    <div v-for="category in filteredCategories" :key="category" :id="'category-' + category" class="category-section">
      <div class="category-header">
        <font-awesome-icon :icon="getCategoryIcon(category)" class="category-icon" />
        <h2>{{ category }}</h2>
      </div>
      <div class="tools-list">
        <div
          v-for="tool in tools.filter(tool => tool.category === category)"
          :key="tool.id"
          class="tool-card"
          @click="navigateToTool(tool)" 
        >
          <img :src="tool.icon" class="tool-icon" />
          <div class="tool-info">
            <div class="tool-name">{{ tool.name }}</div>
            <div class="tool-description">{{ truncate(tool.description) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <footer class="footer">
    <a href="https://beian.miit.gov.cn" target="_blank" class="footer-link">
      <p>琼ICP备:2024038950号</p>
    </a>  
    </footer>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faLanguage, faUserAlt,faUsers, faSchool, faLayerGroup, faBalanceScale, faProjectDiagram, faPencilAlt, faBook, faImage, faRobot, faChartLine, faUserGraduate, faMusic, faPlay, faFilm, faVideo, faSearch, faLaptopCode, faChalkboardTeacher } from '@fortawesome/free-solid-svg-icons';

export default {
  props: {
    selectedCategory: {
      type: String,
      default: '全部',
    },
    isMobile: {
      type: Boolean,
      default: false,
    }
  },
  components: {
    FontAwesomeIcon,
  },
  created() {
    library.add(faLanguage, faUserAlt, faUsers, faSchool, faLayerGroup, faBalanceScale, faProjectDiagram, faPencilAlt, faBook, faImage, faRobot, faChartLine, faUserGraduate, faMusic, faPlay, faFilm, faVideo, faSearch, faLaptopCode, faChalkboardTeacher);
  },
  data() {
    return {
      localSelectedCategory: this.selectedCategory,
      searchQuery: '',
      categories: [],  // 删除了'全部'
      tools: [],
      baseUrl: 'https://mengzi.chat/api/ai/tools', // 替换为你后端服务的 URL
    };
  },
  computed: {
    filteredCategories() {
      return this.categories;  // 不再过滤'全部'
    },
    filteredTools() {
      return this.tools.filter((tool) => {
        const matchesSearch = tool.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                              tool.description.toLowerCase().includes(this.searchQuery.toLowerCase());
        const matchesCategory = this.localSelectedCategory === '全部' || tool.category === this.localSelectedCategory;
        return matchesSearch && matchesCategory;
      });
    }
  },
  watch: {
    selectedCategory(newCategory) {
      this.localSelectedCategory = newCategory; // 同步父组件的值
    }
  },
  mounted() {
    this.fetchTools();
  },
  methods: {
    async fetchTools() {
      try {
        const response = await axios.get(`${this.baseUrl}/tools`);
        if (response.status === 200 && response.data) {
          this.tools = response.data;
          // 只在类别中添加实际的类别，去除'全部'
          this.categories = [...new Set(this.tools.map(tool => tool.category))];
        } else {
          alert('获取工具列表失败');
        }
      } catch (error) {
        alert('获取工具列表出错');
      }
    },
    selectCategory(category) {
  this.localSelectedCategory = category;
  this.$emit('update:selectedCategory', category);

  // 使用 nextTick 确保 DOM 已更新
  this.$nextTick(() => {
    const targetSection = document.querySelector(`#category-${category}`);
    if (targetSection) {
      targetSection.scrollIntoView({ behavior: 'smooth' });
    }
  });
},
    navigateToTool(tool) {
      const toolUrl = tool.url || ''; // 从工具数据中获取url
      if (toolUrl) {
        window.open(toolUrl, '_blank'); // 跳转到工具的官网
      } else {
        alert('工具网址未提供');
      }
    },
    truncate(text) {
      if (!text) return '';
      return text.length > 30 ? text.substring(0, 30) + '...' : text;
    },
    getCategoryIcon(category) {
      switch (category) {
        case '文本生成':
          return ['fas', 'book'];
        case '图像生成':
          return ['fas', 'image'];
        case '视频生成':
          return ['fas', 'video'];
        case '搜索引擎':
          return ['fas', 'search'];
        case '编程助手':
          return ['fas', 'laptop-code'];
        case '视频剪辑':
          return ['fas', 'film'];
        case '办公助手':
          return ['fas', 'chalkboard-teacher'];
        case '音乐生成':
          return ['fas', 'music'];  
        case '海报设计':
          return ['fas', 'layer-group'];
        case '法律顾问':
          return ['fas', 'balance-scale'];  
        case '数据分析':
          return ['fas', 'chart-line']; 
        case '会议工具':
          return ['fas', 'users'];    
        case '思维导图':
          return ['fas', 'project-diagram'];  
        case '写作工具':
          return ['fas', 'pencil-alt'];  
        case '智能体':
          return ['fas', 'robot']; 
        case '教育辅助':
          return ['fas', 'school']; 
        case '语言翻译':
          return ['fas', 'language']; 
        case '数字人':
          return ['fas', 'user-alt'];     
        default:
          return ['fas', 'book'];
      }
    }
  }
};
</script>

<style scoped>
.main-content {
  min-height: 100vh;
  padding-bottom: 50px;  /* 留出空间给备案号 */
}

/* 底部备案号区域 */
.footer {
  background-color: #f1f1f1;
  text-align: center;
  padding: 10px 0;
  font-size: 14px;
  color: #999;
  position: relative;
  bottom: 0;
}

/* 顶部分类区域的卡片样式，增加悬浮效果 */
.categories {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  justify-content: center;
}

.category-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: rgba(255, 255, 255, 0.3);
  color: #333;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.category-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.category-item.active {
  background-color: rgba(255, 255, 255, 0.6);
}

.category-icon {
  font-size: 1.4em;
}

/* 分类部分标题样式 */
.category-section {
  margin-bottom: 40px;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 1em;
  font-weight: bold;
  margin-bottom: 20px;
}

/* 工具列表样式调整 */
.tools-list {
  display: grid;
  grid-template-columns: repeat(5, 1fr); /* 每行显示五个工具卡片 */
  gap: 20px;
  overflow-y: auto;
}

/* 工具卡片样式调整 */
.tool-card {
  background-color: #fff;
  border-radius: 8px; /* 较小的圆角 */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* 轻微阴影，保证看起来精致 */
  padding: 10px; /* 减少内边距，使卡片更小 */
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.tool-card:hover {
  transform: translateY(-3px); /* 鼠标悬停时的浮动效果，略微轻一点 */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15); /* 悬停时加深阴影，增加视觉效果 */
}

.tool-icon {
  width: 40px; /* 缩小图标尺寸 */
  height: 40px;
  object-fit: cover;
  border-radius: 5px; /* 减少圆角，使图标紧凑 */
  margin-bottom: 8px; /* 减少图标与文本之间的间距 */
}

.tool-info {
  text-align: center;
}

.tool-name {
  font-size: 14px; /* 缩小字体以适应较小的卡片 */
  font-weight: bold;
  color: #333;
}

.tool-description {
  font-size: 10px; /* 减小描述的字体大小以适应卡片缩小的设计 */
  color: #666;
  margin-top: 5px;
}

/* 适应移动端的样式 */
@media (max-width: 767px) {
  .categories {
    justify-content: center;
  }

  .category-item {
    padding: 8px 16px;
    font-size: 14px;
  }

  .tools-list {
    grid-template-columns: repeat(3, 1fr); /* 在移动端每行显示三个卡片 */
    gap: 15px;
  }

  .tool-card {
    padding: 10px;
  }

  .tool-icon {
    width: 50px;
    height: 50px;
  }

  .tool-name {
    font-size: 14px;
  }

  .tool-description {
    font-size: 10px;
  }
}

@media (max-width: 425px) {
  .main-content {
    padding: 10px;
  }

  .category-item {
    padding: 6px 12px;
    font-size: 12px;
  }

  .tools-list {
    grid-template-columns: repeat(3, 1fr); /* 保持在小屏幕下每行显示三个卡片 */
    gap: 10px;
  }

  .tool-card {
    padding: 8px;
  }

  .tool-icon {
    width: 40px;
    height: 40px;
  }

  .tool-name {
    font-size: 12px;
  }

  .tool-description {
    font-size: 9px;
  }
}

@media (max-width: 375px) {
  .main-content {
    padding: 8px;
  }

  .tools-list {
    grid-template-columns: repeat(3, 1fr); /* 保持在小屏幕下每行显示三个卡片 */
    gap: 8px;
  }

  .tool-card {
    padding: 6px;
  }

  .tool-icon {
    width: 35px;
    height: 35px;
  }

  .tool-name {
    font-size: 11px;
  }

  .tool-description {
    font-size: 8px;
  }
}

/* 超小屏幕样式（如 320px） */
@media (max-width: 480px) {
  .tools-list {
    grid-template-columns: repeat(3, 1fr); /* 保持在小屏幕下每行显示三个卡片 */
    gap: 8px; /* 减少卡片之间的间距 */
  }
}
</style>